import {
  IconAlertTriangle,
  IconBookmarkPlus,
  IconDeviceFloppy,
  IconEdit,
  IconPointFilled,
  IconRestore,
  IconTrash,
  IconX,
} from '@u21/tabler-icons';
import {
  U21MenuButton,
  U21SplitButton,
  U21Tooltip,
} from 'app/shared/u21-ui/components';
import styled, { css } from 'styled-components';

import { Filter, FilterContext, SavedFilter } from 'app/modules/filters/models';
import { Dispatch, SetStateAction, useState } from 'react';
import { useSelector } from 'react-redux';
import { CreateSavedFilterModal } from 'app/modules/filters/components/CreateSavedFilterModal';
import { EditSavedFilterModal } from 'app/modules/filters/components/EditSavedFilterModal';
import { DeleteSavedFilterModal } from 'app/modules/filters/components/DeleteSavedFilterModal';
import { selectAgent } from 'app/modules/session/selectors';
import { useUpdateSavedFilter } from 'app/modules/filters/queries/useUpdateSavedFilter';
import { alpha } from '@mui/system/colorManipulator';

interface Props {
  defaultFilters: Filter[];
  dirty: boolean;
  filterContext: FilterContext;
  filters: Filter[];
  isStale: boolean;
  onAppliedSavedFilterChange: Dispatch<SetStateAction<SavedFilter | undefined>>;
  onFiltersChange: (filters: Filter[]) => void;
  savedFilter: SavedFilter;
}

export const AppliedSavedFilterButton = ({
  defaultFilters,
  dirty,
  filterContext,
  filters,
  isStale,
  onAppliedSavedFilterChange,
  onFiltersChange,
  savedFilter,
}: Props) => {
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const {
    mutateAsync: updateSavedFilter,
    isPending: isUpdateSavedFilterPending,
  } = useUpdateSavedFilter();

  const agent = useSelector(selectAgent);
  const isOwner = savedFilter.created_by_id === agent.id;
  const notOwnerTooltip = isOwner
    ? undefined
    : `Only ${savedFilter.created_by_name} can edit or delete this saved filter.`;

  let tooltip: string | undefined;
  if (isStale) {
    tooltip = 'Some filters in this saved filter may be unavailable.';
  } else if (dirty) {
    tooltip = `Unsaved changes to ${savedFilter.title}`;
  }

  return (
    <>
      <U21Tooltip tooltip={tooltip}>
        <StyledU21SplitButton
          $dirty={dirty}
          alignRight
          disableTitleCase
          endIcon={dirty ? <StyledIconPointFilled /> : undefined}
          loading={isUpdateSavedFilterPending}
          startIcon={isStale ? <StaleIcon /> : undefined}
          variant="ghost"
          items={[
            ...(dirty
              ? [
                  {
                    icon: <IconDeviceFloppy />,
                    text: 'Update filters',
                    onClick: async () => {
                      try {
                        const updated = await updateSavedFilter({
                          id: savedFilter.id,
                          filters,
                        });
                        onAppliedSavedFilterChange(updated);
                      } catch {}
                    },
                  },
                  {
                    icon: <IconBookmarkPlus />,
                    text: 'Save as new filter',
                    onClick: () => setCreateOpen(true),
                  },
                  {
                    icon: <IconRestore />,
                    text: 'Discard changes',
                    onClick: () => onFiltersChange(savedFilter.filters),
                  },
                  <U21MenuButton.Divider key="divider" />,
                ]
              : []),
            {
              disabled: !isOwner,
              icon: <IconEdit />,
              text: 'Edit',
              tooltip: notOwnerTooltip,
              onClick: () => setEditOpen(true),
            },
            {
              color: 'error' as const,
              disabled: !isOwner,
              icon: <IconTrash />,
              text: 'Delete',
              tooltip: notOwnerTooltip,
              onClick: () => setDeleteOpen(true),
            },
            {
              icon: <IconX />,
              text: 'Clear filter',
              onClick: () => {
                onAppliedSavedFilterChange(undefined);
                onFiltersChange(defaultFilters);
              },
            },
          ]}
        >
          {savedFilter.title}
        </StyledU21SplitButton>
      </U21Tooltip>
      <CreateSavedFilterModal
        filterContext={filterContext}
        filters={filters}
        onClose={() => setCreateOpen(false)}
        onSaved={onAppliedSavedFilterChange}
        open={createOpen}
      />
      <EditSavedFilterModal
        filters={filters}
        onClose={() => setEditOpen(false)}
        onUpdated={onAppliedSavedFilterChange}
        open={editOpen}
        savedFilter={savedFilter}
      />
      <DeleteSavedFilterModal
        filterContext={filterContext}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => onAppliedSavedFilterChange(undefined)}
        open={deleteOpen}
        savedFilter={savedFilter}
      />
    </>
  );
};

const StyledU21SplitButton = styled(U21SplitButton)<{ $dirty: boolean }>`
  border-radius: 8px 8px 0 0;
  border-bottom: 2px solid ${(props) => props.theme.palette.primary.main};

  & .MuiToggleButtonGroup-firstButton {
    // mimics U21Tabs padding
    padding: 0 12px;
    border-bottom-left-radius: 0;
    color: ${(props) => props.theme.palette.primary.main};
    ${(props) =>
      props.$dirty &&
      css`
        font-style: italic;
      `}

    background-color: ${(props) =>
      alpha(props.theme.palette.primary.main, 0.08)};

    &:hover {
      background-color: ${(props) =>
        alpha(props.theme.palette.primary.main, 0.16)};
    }
  }

  & .MuiToggleButtonGroup-lastButton {
    border-bottom-right-radius: 0;
    color: ${(props) => props.theme.palette.primary.main};
    border-left-color: ${(props) =>
      alpha(props.theme.palette.primary.main, 0.48)};

    background-color: ${(props) =>
      alpha(props.theme.palette.primary.main, 0.08)};

    &:hover {
      background-color: ${(props) =>
        alpha(props.theme.palette.primary.main, 0.16)};
    }
  }
`;

const StaleIcon = styled(IconAlertTriangle)`
  color: ${(props) => props.theme.palette.warning.main};
`;

const StyledIconPointFilled = styled(IconPointFilled)`
  color: ${(props) => props.theme.palette.warning.main};
`;
